<script setup lang="ts">
import { computed, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type { PodcastType, PodcastPayloadType } from "@/common/types/podcast";

import {
  baseInput,
  baseSelect,
  baseTextarea,
  baseTextEditor,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: PodcastType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  status: "",
  title: "",
  subtitle: "",
  episode: "",
  duration: "",
  author: "",
  explicit: "",
  summary: "",
  keywords: null,
  description: null,
};

const version = ref(1);

const payload = ref<PodcastPayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  Object.keys(DEFAULT_DATA).forEach((key) => {
    payload.value[key] = props.data[key];
  });
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toUpperCase();
}

function maskSlug(data: string) {
  return data
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll(" ", "-")
    .toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watch(
  () => props.data,
  () => {
    version.value++;
  },
  { immediate: true },
);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-podcast">
    <base-input
      v-model="payload.title"
      :value="payload.title"
      required
      @update:modelValue="debouceUpdate('title')"
    >
      <template #label> {{ t("podcast.labels.title") }} * </template>
    </base-input>

    <base-input
      v-model="payload.subtitle"
      :value="payload.subtitle"
      required
      @update:modelValue="debouceUpdate('subtitle')"
    >
      <template #label> {{ t("podcast.labels.subtitle") }} * </template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionsStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }}</template>
    </base-select>

    <base-input
      v-model="payload.author"
      :value="payload.author"
      required
      @update:modelValue="debouceUpdate('author')"
    >
      <template #label> {{ t("podcast.labels.author") }} * </template>
    </base-input>

    <base-input
      v-model="payload.episode"
      :value="payload.episode"
      required
      @update:modelValue="debouceUpdate('episode')"
    >
      <template #label> {{ t("podcast.labels.episode") }} * </template>
    </base-input>

    <base-textarea
      v-model="payload.keywords"
      :value="payload.keywords"
      @update:modelValue="debouceUpdate('keywords')"
    >
      <template #label> {{ t("podcast.labels.keywords") }}</template>
    </base-textarea>

    <base-text-editor
      :key="version"
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("podcast.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-podcast {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
